import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Dialog } from '@packages/shared';
import { useConfig, useCookieConsent } from '@packages/utilities';
import { Video32 } from '@packages/themes/icons';
import { CliplisterFragmentFragmentDoc } from '@packages/gql/generated/shopping/CliplisterFragmentFragmentDoc';
import { messagesCliplister } from './messages';
import { CliplisterVideo } from './CliplisterVideo';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton';
import { addCliplisterScript } from './addCliplisterScript';
import { useVideoTracking } from '../Tracking/hooks/useVideoTracking';

/* GraphQL */ `
  fragment CliplisterFragment on Product {
    orderNumber
    cliplister(lang: $lang) @skip(if: $skipCliplister) {
      url
    }
  }
`;

export type CliplisterProps = {
  data: FragmentType<typeof CliplisterFragmentFragmentDoc>;
};

/**
 * <p>
 * This component is used to show product videos from cliplister.
 * </p>
 * <p>
 * It originally shows an icon. After it is clicked a `Dialog` opens, which shows a product preview image.
 * After the image is clicked, the player is appended and starts playing the product video.
 *
 * This components needs to have Cookie Consent 4 (marketing) in order to be displayed.
 * <a href="/story/docs-storybooksetup--page">Here</a> you can see how you can simulate the consent in storybook.
 * </p>
 *
 */

export const Cliplister = ({ data }: CliplisterProps) => {
  const { orderNumber, cliplister } = getFragmentData(CliplisterFragmentFragmentDoc, data);
  const {
    product: {
      cliplister: { scriptUrl, consentGroup, enabled },
    },
  } = useConfig();
  // scoped two-way rendering to avoid hydration errors because of consent cookie dependecy
  const [rendered, setRendered] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [scriptLoaded, setScriptloaded] = useState(false);

  const isCookieAllowed = useCookieConsent(consentGroup);

  const { formatMessage } = useIntl();
  const { trackGlycerinDisplayVideo, trackVideoIconClick } = useVideoTracking();

  const isCliplisterAllowed = rendered && enabled && !!cliplister?.url && isCookieAllowed;
  const isCliplisterAvailable = isCliplisterAllowed && scriptLoaded;

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (isCliplisterAllowed) {
      addCliplisterScript(scriptUrl)
        .then(() => setScriptloaded(true))
        .catch(() => setScriptloaded(false));
    }
  }, [isCliplisterAllowed, scriptUrl]);

  useEffect(() => {
    if (isCliplisterAvailable) {
      trackGlycerinDisplayVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCliplisterAvailable, orderNumber]);

  if (!isCliplisterAvailable) return null;

  const openModalHandler = () => {
    trackVideoIconClick();

    setOpenDialog(true);
  };

  return (
    <>
      <ImageGalleryCallToActionButton
        onClick={openModalHandler}
        aria-label={formatMessage(messagesCliplister.buttonLabel)}
      >
        <Video32 sx={{ fill: (theme) => theme.palette.text.dark }} />
      </ImageGalleryCallToActionButton>
      <Dialog
        open={openDialog}
        handleClose={() => {
          setOpenDialog(false);
        }}
        title={formatMessage(messagesCliplister.cliplisterModalTitle)}
        dialogContent={<CliplisterVideo orderNumber={orderNumber} />}
        displayTrackingProps={{ category: 'cliplister-dialog' }}
      />
    </>
  );
};
